import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import PublicNav from '../../../components/layout/PublicNav';

/**
 * Forum Component
 * 💬 Coming soon page for community forum
 */
const Forum = () => {
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Simulating API call for forum interest
    setTimeout(() => {
      toast.success(`Thanks for your interest in the "${topic}" forum! We'll notify you when it launches.`);
      setEmail('');
      setIsSubmitting(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6C5DD3] to-[#8247E5]">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white bg-opacity-95 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
          <div className="p-8 md:p-12">
            <PublicNav />

            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold text-[#6C5DD3] mb-4">Community Forum Coming Soon</h1>
              <div className="h-1 w-24 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] mx-auto rounded-full mb-6"></div>
              <p className="text-gray-700 text-lg max-w-2xl mx-auto">
                We're building a vibrant community forum to connect users, share financial strategies,
                and help couples navigate financial decisions together.
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-xl mb-10">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/2">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    Share Financial Wisdom
                  </h2>
                  <p className="text-gray-700 mb-4">
                    Our forum will be a safe space for couples to discuss financial challenges,
                    share successful strategies, and learn from each other's experiences.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-[#6C5DD3]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                      </svg>
                      <span>Budget planning discussions</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-[#6C5DD3]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                      </svg>
                      <span>Debt reduction strategies</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-[#6C5DD3]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                      </svg>
                      <span>Financial communication tips</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-[#6C5DD3]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                      </svg>
                      <span>Partner agreement templates</span>
                    </li>
                  </ul>
                </div>
                <div className="md:w-1/2">
                  <img 
                    src="https://placehold.co/600x400/6C5DD3/FFFFFF/png?text=Financial+Community&font=montserrat" 
                    alt="Community Forum Preview" 
                    className="rounded-xl shadow-md w-full max-w-md mx-auto"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-10">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                Planned Forum Categories
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50 hover:bg-purple-50 transition duration-200">
                  <h4 className="font-medium text-gray-800 mb-1">Budget Planning</h4>
                  <p className="text-gray-600 text-sm">Creating and managing budgets as partners</p>
                </div>
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50 hover:bg-purple-50 transition duration-200">
                  <h4 className="font-medium text-gray-800 mb-1">Saving Strategies</h4>
                  <p className="text-gray-600 text-sm">Working together toward financial goals</p>
                </div>
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50 hover:bg-purple-50 transition duration-200">
                  <h4 className="font-medium text-gray-800 mb-1">Income Planning</h4>
                  <p className="text-gray-600 text-sm">Managing multiple income sources</p>
                </div>
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50 hover:bg-purple-50 transition duration-200">
                  <h4 className="font-medium text-gray-800 mb-1">Money Talks</h4>
                  <p className="text-gray-600 text-sm">Communication about finances in relationships</p>
                </div>
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50 hover:bg-purple-50 transition duration-200">
                  <h4 className="font-medium text-gray-800 mb-1">Feature Requests</h4>
                  <p className="text-gray-600 text-sm">Suggestions for improving MnyMny</p>
                </div>
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50 hover:bg-purple-50 transition duration-200">
                  <h4 className="font-medium text-gray-800 mb-1">Success Stories</h4>
                  <p className="text-gray-600 text-sm">Share your financial wins and milestones</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-100 to-indigo-100 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Join the Waiting List
              </h3>
              <p className="text-gray-700 text-center mb-6">
                Be the first to know when our community forum launches and get early access to discussions.
              </p>
              <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
                <div className="space-y-4">
                  <div>
                    <input 
                      type="email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address" 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm mb-2">
                      Which forum topic interests you most?
                    </label>
                    <select
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900"
                    >
                      <option value="general">General Discussion</option>
                      <option value="budgeting">Budget Planning</option>
                      <option value="saving">Saving Strategies</option>
                      <option value="income">Income Planning</option>
                      <option value="communication">Money Talks</option>
                      <option value="features">Feature Requests</option>
                      <option value="success">Success Stories</option>
                    </select>
                  </div>
                  <div className="flex justify-center">
                    <button 
                      type="submit"
                      disabled={isSubmitting} 
                      className="px-8 py-3 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] text-white font-medium rounded-xl hover:opacity-90 transition duration-200 disabled:opacity-70"
                    >
                      {isSubmitting ? 'Joining...' : 'Join Waiting List'}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div className="mt-10 text-center text-gray-600">
              <p>
                Want to start using MnyMny now?{' '}
                <Link to="/login" className="text-[#6C5DD3] hover:underline">
                  Sign in
                </Link>
                {' '}or{' '}
                <Link to="/register" className="text-[#6C5DD3] hover:underline">
                  create an account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forum; 